import { IPricing, OwnPricingApiClient } from 'api/OwnPricingApiClient'
import { useApiClient } from '@hooks/useApiClient'
import { useEffect, useState } from 'react'

export interface AgreementPricingState {
    pricing: IPricing | undefined
    useCustom: boolean
    isLoading: boolean
    isUpdating: boolean
    isBusy: boolean
    isInitialized: boolean
    isDirty: boolean
    refresh: () => Promise<void>
    save: (pricing: IPricing) => Promise<void>
    deleteCustom: () => Promise<void>
    setUseCustom: (value: boolean) => void
}

export default function useAgreementPricingByAgreement(
    dealerId: number | undefined,
    agreementId: number
): AgreementPricingState {
    const ownPricingApi = useApiClient(x => new OwnPricingApiClient(x))
    const [pricing, setPricing] = useState<IPricing>()
    const [useCustom, setUseCustom] = useState(false)
    const [serverHasCustom, setServerHasCustom] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isUpdating, setIsUpdating] = useState(false)

    const refresh = () => {
        setIsLoading(true)
        return ownPricingApi.getAgreementPricingByAgreementId(dealerId!, agreementId)
            .then(response => {
                if (response && response.agreementId != null) {
                    setPricing(response)
                    setUseCustom(true)
                    setServerHasCustom(true)
                } else {
                    setPricing(undefined)
                    setUseCustom(false)
                    setServerHasCustom(false)
                }
            })
            .finally(() => setIsLoading(false))
    }

    const save = (pricingValues: IPricing) => {
        setIsUpdating(true)
        return ownPricingApi.updateAgreementPricingByAgreementId(dealerId!, agreementId, pricingValues)
            .then(() => refresh())
            .finally(() => setIsUpdating(false))
    }

    const deleteCustom = () => {
        setIsUpdating(true)
        return ownPricingApi.deleteAgreementPricingByAgreementId(dealerId!, agreementId)
            .then(() => {
                setPricing(undefined)
                setUseCustom(false)
                setServerHasCustom(false)
            })
            .finally(() => setIsUpdating(false))
    }

    useEffect(() => {
        if (!dealerId) {
            return
        }
        refresh()
    }, [dealerId])

    const isBusy = isLoading || isUpdating
    const isInitialized = !isLoading
    const isDirty = useCustom !== serverHasCustom

    return { pricing, useCustom, isLoading, isUpdating, isBusy, isInitialized, isDirty, refresh, save, deleteCustom, setUseCustom }
}
